import type {
  CreateCsvTemplateArgs,
  CreateCsvTemplateRequestDto,
  CsvColumnMappingDto,
  CsvTemplateDto,
} from './csv-templates.api.types';
import type { CsvColumnMapping, CsvCustomerField, CsvTemplate } from './types';

const CUSTOMER_FIELD_KEYS = {
  birthYear: 'birth_year',
  email: 'email',
  externalId: 'external_id',
  externalNodeId: 'external_node_id',
  gender: 'gender',
  name: 'name',
  phoneNumber: 'phone_number',
} as const satisfies Record<CsvCustomerField, keyof CsvColumnMappingDto>;

const customerFields = Object.keys(CUSTOMER_FIELD_KEYS) as CsvCustomerField[];

// Unmapped fields and custom attributes are left out, as legacy does.
export function toApiCsvColumnMapping({
  customAttributes,
  customerFields: fields,
}: CsvColumnMapping): CsvColumnMappingDto {
  const mapping: CsvColumnMappingDto = {};

  customerFields.forEach((field) => {
    const column = fields[field];

    if (column) {
      mapping[CUSTOMER_FIELD_KEYS[field]] = column;
    }
  });

  if (customAttributes.length) {
    mapping.custom_attrs = Object.fromEntries(customAttributes.map(({ column, key }) => [key, column]));
  }

  return mapping;
}

export function fromApiCsvColumnMapping(mapping: CsvColumnMappingDto | null): CsvColumnMapping {
  const fields: CsvColumnMapping['customerFields'] = {};

  customerFields.forEach((field) => {
    const column = mapping?.[CUSTOMER_FIELD_KEYS[field]];

    if (column) {
      fields[field] = column;
    }
  });

  return {
    customAttributes: Object.entries(mapping?.custom_attrs ?? {}).map(([key, column]) => ({ column, key })),
    customerFields: fields,
  };
}

export function fromApiCsvTemplate({ id, mapping, name }: CsvTemplateDto): CsvTemplate {
  return { id, mapping: fromApiCsvColumnMapping(mapping), name };
}

export function toApiCreateCsvTemplate({
  mapping,
  name,
  type,
}: Omit<CreateCsvTemplateArgs, 'accountId'>): CreateCsvTemplateRequestDto {
  return { mapping: toApiCsvColumnMapping(mapping), name, type };
}
